/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React from 'react';
import type { TrackingDataV2 } from '@oneaudi/tracking-service';
import { useLogger, useTrackingService, useAppContext } from './Context';
import { checkLinkTarget, completeTargetURL } from '../utils/trackingUtils';

export interface CtaTexts {
  primary: string;
  secondary: string;
}

export interface AppliedFilter {
  id: string;
  values: string[] | number[];
}

export interface TrackingManager {
  ready(version: string): void;
  impression(): void;
  click(
    targetURL: string,
    label: string,
    linkValue: string,
    carlineGroup: string,
    carline: string,
    amount?: number,
  ): void;
  trackTagClick(label: string, value: string): void;
  trackExpandCardClick(value: string, id: string, element: string, clickID: string): void;
  trackCTAClick(
    targetURL: string,
    label: string,
    ctaType: string,
    value: string,
    clickID: string,
    elementName?: 'tag' | 'text link' | 'button' | 'image' | 'icon' | 'other',
    amount?: number,
  ): void;
  trackCarlineImageClick(value: string): void;
  trackScrollOrSwipeEvent(clickID: string, actionType: 'click' | 'swipe'): void;
}

export interface TrackingEvent {
  event: {
    eventInfo: {
      eventAction: string;
      eventName: string;
    };
    attributes: Record<string, unknown>;
  };
  componentUpdate: {
    componentInfo: {
      implementer: number;
      version: string;
    };
  };
}

function createTrackingPayload(
  action: string,
  name: string,
  attributes: Record<string, unknown>,
): TrackingDataV2 {
  const defaultAttributes = {
    componentName: 'lean-moxx',
    label: '',
    targetURL: '',
    clickID: '',
    value: '',
    pos: '',
    actionType: '',
  };

  return {
    event: {
      eventInfo: {
        eventAction: action,
        eventName: name,
      },
      attributes: {
        ...defaultAttributes,
        ...attributes,
      },
    },
  };
}

function createComponentPayload(version: string) {
  return {
    componentInfo: {
      implementer: 2,
      version,
    },
  };
}

export function useTrackingManager(): TrackingManager {
  const logger = useLogger();
  const trackingService = useTrackingService();
  const { selectedCarGroupId } = useAppContext();

  return React.useMemo((): TrackingManager => {
    function track(...args: Parameters<typeof createTrackingPayload>): void {
      if (trackingService) {
        const payload = createTrackingPayload(...args);
        if (logger) logger.debug('Sending tracking event: ', payload);
        trackingService.track(payload);
      }
    }

    function trackWithComponent(
      version: string,
      ...args: Parameters<typeof createTrackingPayload>
    ): void {
      if (trackingService) {
        const payload = createTrackingPayload(...args);

        const componentPayload = createComponentPayload(version);

        logger?.debug('Sending tracking event: ', {
          ...payload,
          datalayerAdditions: componentPayload,
        });

        const x: TrackingDataV2 = {
          ...payload,
          componentUpdate: componentPayload,
        };
        trackingService.track(x);
      }
    }

    return {
      ready(version) {
        trackWithComponent(version, 'feature_app_ready', 'lean moxx - feature app ready', {
          label: '',
          targetURL: '',
          clickID: '',
          elementName: '',
          value: '',
          pos: '',
        });
      },
      impression() {
        track('impression', `lean moxx - impression`, {
          label: '',
          targetURL: '',
          clickID: '',
          elementName: '',
          value: 'All models',
          pos: '',
        });
      },
      click(targetURL, label, linkValue, carlineGroup, carline, amount) {
        track(checkLinkTarget(targetURL), `lean moxx - click on '${linkValue}'`, {
          label,
          targetURL: completeTargetURL(targetURL),
          elementName: 'text link',
          value: `${carlineGroup}~${carline}`,
          pos: amount ? amount?.toString() : '',
        });
      },
      trackTagClick(label, value) {
        track('navigation', 'lean moxx - click to change tag', {
          label,
          targetURL: '',
          clickID: value,
          elementName: 'tag',
          value,
        });
      },
      trackExpandCardClick(value, id, element, clickID) {
        const direction = !selectedCarGroupId || selectedCarGroupId !== id ? 'open' : 'close';
        track('navigation', `lean moxx - click to ${direction} carline group tile`, {
          label: value,
          targetURL: '',
          clickID: clickID,
          elementName: element,
          value: id,
        });
      },
      trackCarlineImageClick(value) {
        track('navigation', 'lean moxx - click on image', {
          elementName: 'image',
          value,
        });
      },
      trackCTAClick(targetURL, label, ctaType, value, clickID, elementName, amount) {
        track(checkLinkTarget(targetURL), `lean moxx - click on '${ctaType}'`, {
          label,
          targetURL: completeTargetURL(targetURL),
          elementName: elementName || 'text link',
          value,
          clickID,
          pos: amount ? amount?.toString() : '',
        });
      },
      trackScrollOrSwipeEvent(clickID, actionType) {
        track('navigation', 'lean moxx - swipe carline group tiles', {
          elementName: 'other',
          clickID,
          actionType,
        });
      },
    };
  }, [trackingService, selectedCarGroupId]);
}
